import {AxiosResponse} from 'axios';
import {finishLoading} from '../connected-components/loading/actions';
import {showGlobalToast} from '../connected-components/toast/actions';
import axiosInstance from './axiosInstance';
import {Result, ToastType} from '../utils/enums';
import {tr} from '../translations';
import {Http, HttpHeaders, HttpResponse} from '../interfaces/api/http';
import {store} from '../redux/store';

/**
 * Retrieves the authentication token from the user's session stored in the Redux state.
 *
 * @returns {string} - The authentication token or an empty string if the token does not exist.
 */
export const getToken = () => {
  const userSession = store.getState().auth;
  return userSession?.currentUser?.token ? userSession?.currentUser?.token : '';
};

/**
 * Error handler function that displays error messages or copies the error to the clipboard (if in debug mode).
 * @param {any} err - The error object.
 * @returns {boolean} Returns true to indicate that the error has been handled.
 */
export const handleError = (err: any) => {
  try {
    finishLoading();
    const message = Array.isArray(err)
      ? err[0]
      : err.response?.data?.message ||
          (err?.data?.error_description &&
            err?.data?.error_description?.length > 0)
        ? err.data?.error_description[0]
        : err?.response?.data?.error_description &&
            err?.response?.data?.error_description?.length > 0
          ? err?.response?.data?.error_description[0]
          : tr('app.error');

    showGlobalToast({
      message,
      type: ToastType.ERROR,
    });
    return true;
  } catch (e) {
    showGlobalToast({
      message: tr('app.error'),
      type: ToastType.ERROR,
    });
    return true;
  }
};

/**
 * Http service that provides methods for making HTTP requests.
 * @type {object}
 */
const http: Http = {
  /**
   * Performs a GET request.
   * @param {string} url - The URL to send the GET request to.
   * @param {HttpHeaders} headers - Optional headers to be included in the request.
   * @param {boolean} showErrorToast - Optional flag to show error toast for the request.
   * @returns {Promise<HttpResponse>} A Promise that resolves to the response data.
   */
  get: (
    url: string,
    headers: HttpHeaders = {},
    showErrorToast: boolean = true,
  ): Promise<HttpResponse> =>
    new Promise((resolve, reject) => {
      axiosInstance
        .get(url, {headers})
        .then((res: AxiosResponse<any>) => {
          if (res.data.result !== Result.SUCCESS && showErrorToast) {
            handleError(res);
          }
          resolve(res.data);
        })
        .catch(err => {
          if (showErrorToast) {
            handleError(err);
          }
          reject(err);
        });
    }),
  /**
   * Performs a POST request.
   * @param {string} url - The URL to send the POST request to.
   * @param {any} body - The request body.
   * @param {HttpHeaders} headers - Optional headers to be included in the request.
   * @param {boolean} showErrorToast - Optional flag to show error toast for the request.
   * @returns {Promise<HttpResponse>} A Promise that resolves to the response data.
   */
  post: (
    url: string,
    body: any,
    headers: HttpHeaders = {},
    showErrorToast: boolean = true,
  ): Promise<HttpResponse> =>
    new Promise((resolve, reject) => {
      axiosInstance
        .post(url, body, {headers})
        .then(res => {
          if (res.data.result !== Result.SUCCESS && showErrorToast) {
            handleError(res);
          }
          resolve(res.data);
        })
        .catch(err => {
          if (showErrorToast) {
            handleError(err);
          }
          reject(err);
        });
    }),
  /**
   * Performs a PUT request.
   * @param {string} url - The URL to send the PUT request to.
   * @param {any} body - The request body.
   * @param {HttpHeaders} headers - Optional headers to be included in the request.
   * @param {boolean} showErrorToast - Optional flag to show error toast for the request.
   * @returns {Promise<HttpResponse>} A Promise that resolves to the response data.
   */
  put: (
    url: string,
    body: any,
    headers: HttpHeaders = {},
    showErrorToast: boolean = true,
  ): Promise<HttpResponse> =>
    new Promise((resolve, reject) => {
      axiosInstance
        .put(url, body, {headers})
        .then(res => {
          if (res.data.result !== Result.SUCCESS) {
            handleError(res);
          }
          resolve(res.data);
        })
        .catch(err => {
          if (showErrorToast) {
            handleError(err);
          }
          reject(err);
        });
    }),
  /**
   * Performs a DELETE request.
   * @param {string} url - The URL to send the DELETE request to.
   * @returns {Promise<HttpResponse>} A Promise that resolves to the response data.
   */
  delete: (url: string): Promise<HttpResponse> =>
    new Promise((resolve, reject) => {
      axiosInstance
        .delete(url)
        .then(res => {
          if (res.data.result !== Result.SUCCESS) {
            handleError(res);
          }
          resolve(res.data);
        })
        .catch(err => {
          handleError(err);
          reject(err);
        });
    }),
};

export default http;
